/**
 * Asset Delivery — 대용량 에셋(온디바이스 모델 등) 배달 플러그인 소비 접합부.
 *
 * 계약: features/asset-delivery/CONTRACT.md §1 (@duckflex/capacitor-asset-delivery).
 * 다운로드/검증/원자 교체/lease/정리는 전부 네이티브가 담당 — TS 는 이벤트 구독+질의만.
 *
 * 흐름:
 *   1. 앱 부팅(또는 온보딩 진입) 시 configureAssetDelivery() 1회.
 *   2. syncAssets() — 진행률은 assetSyncStore 로 흘러 useAssetSync 훅이 소비.
 *   3. 추론 직전 acquireAsset()/releaseAsset() 로 lease 사용.
 *
 * web 환경에서는 no-op (모델 에셋은 네이티브 전용 — sync 는 즉시 done 처리해
 * 온보딩 플로우가 웹 개발 환경에서도 막히지 않게 한다).
 */
import {
  AssetDelivery,
  type AssetDeliveryPlugin,
  type AssetErrorCode,
} from "@duckflex/capacitor-asset-delivery";
import { isNative, nativeWarn } from "./index";
import { getEffectiveApiBase } from "../config/env";
import { useAssetSyncStore } from "../store/assetSyncStore";
import { getAssetToken } from "./assetToken";

// ─── 플러그인 로딩 ──────────────────────────────────────────────────
// 플러그인은 package.json file: 의존성으로 연결됨 (@duckflex/capacitor-asset-delivery).
// web 환경에서는 null(no-op) — 모델 에셋은 네이티브 전용.
// ⚠ 반드시 동기 함수 — async 로 Capacitor 프록시를 return 하면 await 의 thenable
// 검사가 프록시의 .then 을 플러그인 메서드로 호출해 "AssetDelivery.then() is not
// implemented" 로 터진다 (실기기에서 실제 발생. 템플릿 원본도 동일 수정 필요).
function loadPlugin(): AssetDeliveryPlugin | null {
  if (!isNative()) return null;
  return AssetDelivery;
}

/** 플러그인 reject 형태 { code, message } 에서 계약 오류 코드 추출. */
const KNOWN_CODES: readonly AssetErrorCode[] = [
  "NETWORK_RETRYABLE",
  "SERVER_ERROR",
  "HASH_MISMATCH",
  "DISK_FULL",
  "CANCELLED",
  "INCOMPATIBLE",
  "NOT_READY",
];

export function toAssetErrorCode(err: unknown): AssetErrorCode | null {
  if (typeof err === "object" && err !== null) {
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string" && (KNOWN_CODES as readonly string[]).includes(code)) {
      return code as AssetErrorCode;
    }
  }
  return null;
}

// ─── configure ─────────────────────────────────────────────────────

/** manifest 절대 URL (계약 §2 — GET /api/v1/assets/manifest). 상대 base 는 origin 으로 절대화. */
function manifestUrl(): string {
  let base = getEffectiveApiBase().replace(/\/$/, "");
  if (base.startsWith("/") && typeof window !== "undefined") {
    base = window.location.origin + base;
  }
  return `${base}/assets/manifest`;
}

/**
 * 이 앱이 구독하는 스트림 — 호환 경계 단위.
 * liner(검출: card-seg/refine/verifier/config)와 matcher(식별: embed/index/serlno/config)는
 * 서로 공유하는 게 없어 따로 발행된다. 한 릴리스로 묶여 있던 시절, 검출 모델만 갱신하다
 * 매칭 에셋 5종이 통째로 누락돼 카드 식별이 끊긴 적이 있다.
 */
export const ASSET_STREAMS = ["liner", "matcher"] as const;

export interface ConfigureAssetDeliveryOptions {
  /** 배포 채널. 미지정 시 플러그인 기본(PRODUCTION). */
  channel?: "INTERNAL" | "BETA" | "PRODUCTION";
  /** semver 앱 버전 — manifest 호환성(minAppVer) 판정에 쓰인다. */
  appVersion: string;
  /** 이 빌드가 실행 가능한 포맷 코드 (예: ["ONNX_1"]). */
  formats: string[];
  /** 구독할 스트림(= 함께 갱신되어야 하는 파일 묶음). 생략 시 플러그인 기본 ["default"]. */
  streams?: string[];
  /**
   * 선호 variant, 앞이 우선. 서버가 kind 당 하나만 골라 내린다 —
   * 언어팩처럼 택일 자산의 전송량을 줄이는 지점이 여기다(lease 가 아니다).
   */
  variants?: string[];
  /**
   * manifest·파일 다운로드 Authorization Bearer.
   *
   * ⚠ **평소에는 넘기지 않는다** — `configureAssetDelivery` 가 `assetToken.ts` 에서 스스로
   * 조달한다(캐시·선제갱신 포함). 여기 값을 주면 그 조달을 건너뛴다: 테스트에서 특정
   * 토큰을 강제할 때만 쓴다.
   */
  authToken?: string;
}

let configured = false;
let progressListenerBound = false;

/**
 * 플러그인 설정 — 앱 부팅 시 1회. 성공 여부 반환 (web/미탑재 환경 false).
 * 진행률 리스너도 여기서 1회 바인딩한다 (이후 모든 sync 가 store 로 흐름).
 */
export async function configureAssetDelivery(
  opts: ConfigureAssetDeliveryOptions,
): Promise<boolean> {
  const plugin = loadPlugin();
  if (!plugin) return false;
  try {
    // 토큰은 여기서 조달한다 — 호출부는 모른다(assetToken.ts 주석 참조).
    // 실패하면 null 이고, 그대로 진행한다: 게이트가 켜진 환경에서는 sync 만 실패하고
    // 이미 받아둔 에셋의 lease(acquireAsset)는 configure 와 무관하게 계속 동작한다.
    const authToken = opts.authToken ?? (await getAssetToken()) ?? undefined;
    await plugin.configure({
      manifestUrl: manifestUrl(),
      channel: opts.channel,
      appVersion: opts.appVersion,
      formats: opts.formats,
      streams: opts.streams,
      variants: opts.variants,
      authToken,
    });
    if (!progressListenerBound) {
      await plugin.addListener("assetProgress", (event) => {
        useAssetSyncStore.getState().applyEvent(event);
      });
      progressListenerBound = true;
    }
    configured = true;
    return true;
  } catch (err) {
    nativeWarn("assetDelivery:configure", err);
    return false;
  }
}

// ─── sync ──────────────────────────────────────────────────────────

export interface AssetStreamSyncResult {
  stream: string;
  releaseId: number;
  releaseVer: string;
  downloaded: number;
  skipped: number;
  /** 그 스트림만 실패했을 때의 코드. 나머지 스트림은 정상 진행된다. */
  errorCode?: AssetErrorCode;
}

export interface AssetSyncResult {
  streams: AssetStreamSyncResult[];
}

/**
 * 에셋 동기화 — manifest 조회 → 필요 파일 다운로드 → 검증 → 원자 교체.
 * 재진입 시 진행 중 sync 에 합류한다(코어 규칙 7) — 중복 호출 무해.
 * 성공 시 결과 반환, 실패 시 null (오류 코드는 assetSyncStore.errorCode).
 * web 환경: 즉시 done 처리 후 null 반환 (온보딩 플로우 통과용).
 */
export async function syncAssets(): Promise<AssetSyncResult | null> {
  const store = useAssetSyncStore.getState();
  const plugin = loadPlugin();
  if (!plugin) {
    // web / 플러그인 미탑재 — 진행률 UI 가 무한 대기하지 않도록 즉시 완료 처리.
    store.finishDone("web-noop");
    return null;
  }
  if (!configured) {
    nativeWarn("assetDelivery:sync", new Error("configure 전에 sync 호출 (NOT_READY)"));
    store.fail("NOT_READY");
    return null;
  }
  store.begin();
  try {
    const result = await plugin.sync();
    // 진행률 표시는 스트림 하나가 아니라 세트 전체를 대표해야 한다 —
    // 성공한 스트림의 버전을 이어 붙여 어느 세대가 적용됐는지 남긴다.
    const applied = result.streams.filter((s) => !s.errorCode);
    store.finishDone(applied.map((s) => `${s.stream}:${s.releaseVer}`).join(" "));
    result.streams
      .filter((s) => s.errorCode)
      .forEach((s) => nativeWarn(`assetDelivery:sync:${s.stream}`, new Error(s.errorCode)));
    return result;
  } catch (err) {
    const code = toAssetErrorCode(err);
    nativeWarn("assetDelivery:sync", err);
    useAssetSyncStore.getState().fail(code);
    return null;
  }
}

/**
 * 인식 언어 변경 반영 — 재-configure 후 재-sync.
 *
 * 언어는 lease 가 아니라 manifest 단에서 정해진다(CONTRACT.md §0). 서버가 그 언어의
 * 인덱스 하나만 내려주므로, 바꾸려면 실제로 새 파일을 받아야 한다. 이미 받아둔 언어팩은
 * 해시가 같아 하드링크로 재사용되므로 두 번째부터는 사실상 즉시 전환된다.
 */
export async function applyScanLanguage(opts: ConfigureAssetDeliveryOptions): Promise<void> {
  configured = false;
  const ok = await configureAssetDelivery(opts);
  if (ok) await syncAssets();
}

/** 진행 중 sync 취소 — 플러그인이 CANCELLED 로 reject 하고 진행분은 재개 가능하게 보존된다. */
export async function cancelAssetSync(): Promise<void> {
  const plugin = loadPlugin();
  if (!plugin) return;
  try {
    await plugin.cancelSync();
  } catch (err) {
    nativeWarn("assetDelivery:cancel", err);
  }
}

// ─── lease (getAsset / releaseAsset) ───────────────────────────────

export interface AssetLease {
  /** 네이티브 파일 절대 경로 — releaseAsset 전까지 유효 보장. */
  path: string;
  releaseId: number;
  version: string;
  hash: string;
  leaseId: string;
  /**
   * 보호 방식 (asset-delivery v2). 'AES_GCM_V1' 이면 파일이 DFC1 암호화 컨테이너 —
   * be POST /api/v1/assets/keys/unwrap 으로 DEK 를 받아 엔진에 함께 넘겨야 한다.
   * 구버전 플러그인 응답에는 없을 수 있어 옵셔널.
   */
  protection?: string;
}

/**
 * 에셋 lease 획득. 사용 후 반드시 releaseAssetLease(leaseId) — 미반납 시
 * 해당 세대가 정리 대상에서 제외돼 디스크가 회수되지 않는다.
 * 실패 시 throw ({ code, message } — code 는 toAssetErrorCode 로 판정).
 */
export async function acquireAsset(kind: string, variant?: string): Promise<AssetLease> {
  const plugin = loadPlugin();
  if (!plugin) {
    throw { code: "NOT_READY" as AssetErrorCode, message: "asset-delivery 플러그인 미탑재 (web?)" };
  }
  return plugin.getAsset({ kind, variant });
}

export async function releaseAssetLease(leaseId: string): Promise<void> {
  const plugin = loadPlugin();
  if (!plugin) return;
  try {
    await plugin.releaseAsset({ leaseId });
  } catch (err) {
    nativeWarn("assetDelivery:release", err);
  }
}
